import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { getAllCurrencies } from "../../redux/Actions/AuthAction";
import { escrowAPI } from "../../services/escrowAPI";
import { useCurrencyDetection, getCachedCurrencyPreference, setCachedCurrencyPreference, getDynamicCurrencySymbol, getCurrencySymbol } from "../../lib/currencyUtils";
import { convertCurrency, getCurrencyById } from "../../lib/currencyConversion";
import { Button } from "../ui/Button";
import CurrencyDropdown from "../ui/CurrencyDropdown";
import { X, DollarSign, Loader2, CheckCircle, AlertCircle, Plus, CreditCard, ArrowRight } from "lucide-react";
import { getCustomerTax } from "../../utils/categoryFormSettingsUtils";
import { RoleEnum } from "../../modules/shared/model/role.enum";
import CardPaymentMethodForm from "./CardPaymentMethodForm";
import BankAccountPaymentMethodForm from "./BankAccountPaymentMethodForm";
import { useTranslation } from "react-i18next";

interface AddFundsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddFundsModal: React.FC<AddFundsModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const { paymentMethods, defaultPaymentMethod, refreshPaymentMethods } = usePaymentMethods();
    const { user, currencies } = useAppSelector((state: any) => state.auth);
    const { setting } = useAppSelector((state: any) => state.categoryFormSettings);
    console.log("setting123", setting);
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [showAddCard, setShowAddCard] = useState(false);
    const [showAddBankAccount, setShowAddBankAccount] = useState(false);
    const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
    const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState<string | null>(null);
    const [showConversionInfo, setShowConversionInfo] = useState(false);
    const [baseCurrencyId, setBaseCurrencyId] = useState<string | null>(null); // Track original currency for display

    const [formData, setFormData] = useState({
        amount: "",
        currency: "EUR",
        selectedCurrencyId: "1", // Default to EUR (base currency)
        paymentMethodId: "",
    });

    useEffect(() => {
        setMounted(true);
        if (isOpen) {
            document.body.style.overflow = "hidden";
            // Load currencies if not already loaded
            if (!currencies || currencies.length === 0) {
                dispatch(getAllCurrencies({ isShowLoader: false }));
            }
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen, dispatch, currencies]);

    // Sync selectedCurrency with formData.selectedCurrencyId
    useEffect(() => {
        if (formData.selectedCurrencyId && currencies && currencies.length > 0) {
            if (selectedCurrency?.id !== formData.selectedCurrencyId) {
                const currency = currencies.find((c: any) => c.value === formData.selectedCurrencyId);
                if (currency) {
                    setSelectedCurrency({
                        id: formData.selectedCurrencyId,
                        name: currency.label || "USD",
                        code: currency.initial || "USD"
                    });
                }
            }
        }
    }, [formData.selectedCurrencyId, currencies, selectedCurrency?.id]);

    // Handle currency selection
    const handleCurrencySelect = (currency: {id: string; name: string; code: string} | null) => {
        if (currency) {
            // Store the original base currency if this is the first selection
            if (!baseCurrencyId) {
                setBaseCurrencyId(formData.selectedCurrencyId);
            }

            // Show conversion info if currency is different from detected/initial currency
            const isDifferentCurrency = baseCurrencyId && currency.id !== baseCurrencyId;
            setShowConversionInfo(isDifferentCurrency);

            setSelectedCurrency(currency);
            setFormData(prev => ({
                ...prev,
                selectedCurrencyId: currency.id,
                currency: currency.code
            }));
            // Update cache when user manually changes currency
            setCachedCurrencyPreference(currency.id);
        }
    };

    // Currency detection effect
    useEffect(() => {
        const detectAndSetCurrency = async () => {
            if (!currencies || currencies.length === 0 || !isOpen) return;

            setIsDetectingCurrency(true);
            try {
                // First check for cached preference
                const cachedCurrencyId = getCachedCurrencyPreference();
                if (cachedCurrencyId) {
                    const selectedCurrency = currencies.find((c: any) => c.value === cachedCurrencyId);
                    setFormData(prev => ({
                        ...prev,
                        selectedCurrencyId: cachedCurrencyId,
                        currency: selectedCurrency?.initial || "USD"
                    }));
                    setIsDetectingCurrency(false);
                    return;
                }

                // Detect currency based on user location
                const detectedCurrencyId = await useCurrencyDetection(
                    currencies,
                    (currencyId, source) => {
                        setCachedCurrencyPreference(currencyId);
                    },
                    dispatch // Pass dispatch for backend API call
                );

                const selectedCurrency = currencies.find((c: any) => c.value === detectedCurrencyId);
                setFormData(prev => ({
                    ...prev,
                    selectedCurrencyId: detectedCurrencyId,
                    currency: selectedCurrency?.initial || "USD"
                }));
                // Set as base currency for conversion tracking
                setBaseCurrencyId(detectedCurrencyId);
            } catch (error) {
                console.error("Error detecting currency:", error);
                // Fallback to EUR (base currency)
                const eurCurrency = currencies.find((c: any) => c.initial === "EUR");
                if (eurCurrency) {
                    setFormData(prev => ({
                        ...prev,
                        selectedCurrencyId: eurCurrency.value,
                        currency: "EUR"
                    }));
                    setBaseCurrencyId(eurCurrency.value);
                }
            } finally {
                setIsDetectingCurrency(false);
            }
        };

        detectAndSetCurrency();
    }, [currencies, isOpen]);

    useEffect(() => {
        // Set default payment method when available
        if (defaultPaymentMethod && !formData.paymentMethodId) {
            setFormData(prev => ({ ...prev, paymentMethodId: defaultPaymentMethod.id }));
        }
    }, [defaultPaymentMethod, formData.paymentMethodId]);

    // Auto-select newly added payment method
    useEffect(() => {
        if (pendingPaymentMethodId && paymentMethods.length > 0) {
            const addedMethod = paymentMethods.find(
                method => method.stripePaymentMethodId === pendingPaymentMethodId
            );
            
            if (addedMethod) {
                setFormData(prev => ({ ...prev, paymentMethodId: addedMethod.id }));
                setPendingPaymentMethodId(null); // Clear pending state
            }
        }
    }, [pendingPaymentMethodId, paymentMethods]);

    // Reset states when modal closes
    const handleClose = () => {
        setSuccess(false);
        setError(null);
        setLoading(false);
        setFormData({
            amount: "",
            currency: "USD",
            selectedCurrencyId: "1",
            paymentMethodId: "",
        });
        document.body.style.overflow = "unset";
        onClose();
    };

    if (!mounted || !isOpen) return null;

    const validateForm = () => {
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            setError(t("addFundsModal.minimumAmountError", { symbol: getDynamicCurrencySymbol() }));
            return false;
        }

        if (parseFloat(formData.amount) < 10) {
            setError(t("addFundsModal.minimumAmountIs", { symbol: getDynamicCurrencySymbol() }));
            return false;
        }

        if (!formData.paymentMethodId) {
            setError(t("addFundsModal.selectPaymentMethodError"));
            return false;
        }

        return true;
    };

    const calculateFees = () => {
        const amount = parseFloat(formData.amount) || 0;
        // const platformFee = amount * 0.02; // 2% platform fee
        const customerTax = getCustomerTax(amount, setting); // Customer tax from settings
        const total = amount + customerTax;

        return {
            amount,
            customerTax,
            total
        };
    };

    const handlePaymentMethodAdded = async (newPaymentMethod: any) => {
        // Store the Stripe payment method ID to select after refresh
        setPendingPaymentMethodId(newPaymentMethod.stripePaymentMethodId);
        
        // Refresh payment methods list
        await refreshPaymentMethods();
        
        // Close the add payment method modal
        setShowAddCard(false);
        setShowAddBankAccount(false);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate form first
            if (!validateForm()) {
                return;
            }

            const { amount, customerTax, total } = calculateFees();

            // Determine user role and set appropriate IDs
            const isClient = Number(user?.role?.id) === RoleEnum.client;
            const clientId = isClient ? user.id : null;
            const professionalId = !isClient ? user.id : null;

            // Create escrow record
            const escrowData = {
                amount: amount, // Base amount
                platformFee: customerTax, // 2% platform fee
                customerTax: customerTax, // Customer tax from settings
                totalAmount: total, // Total amount including fee
                withdrawlAmount: amount, // Amount available for withdrawal
                currency: formData.selectedCurrencyId, // Currency ID
            };

            if (isClient) {
                escrowData["clientId"] = clientId;
            } else {
                escrowData["professionalId"] = professionalId;
            }

            let escrow;
            try {
                escrow = await escrowAPI.createEscrow(escrowData);
            } catch (escrowError: any) {
                console.error("Escrow creation error:", escrowError);
                throw new Error(t("addFundsModal.failedToCreateEscrow"));
            }

            // Use the dedicated add funds API endpoint for payment processing
            const selectedCurrency = currencies?.find((c: any) => c.value === formData.selectedCurrencyId);

            const addFundsData = {
                amount: amount,
                currency: selectedCurrency?.code || "usd",
                paymentMethodId: formData.paymentMethodId,
                notes: `Adding funds to wallet - ${selectedCurrency?.name || "USD"} ${amount}`,
                escrowId: escrow.id, // Include escrowId for webhook processing
            };

            try {
                // Call the add funds API
                const result = await escrowAPI.addFunds(addFundsData);

                // Update escrow status to user_funded after successful payment
                await escrowAPI.updateEscrow(escrow.id, {
                    status: "user_funded",
                    stripePaymentIntentId: result.paymentIntentId

                });
            } catch (addFundsError: any) {
                console.error("Add funds API error:", addFundsError);

                // Update escrow status to failed if add funds fails
                try {
                    await escrowAPI.updateEscrow(escrow.id, {
                        status: "failed"
                    });
                } catch (updateError) {
                    console.error("Failed to update escrow status:", updateError);
                }

                // Re-throw the error to be handled by the outer catch block
                throw addFundsError;
            }

            setSuccess(true);
            setTimeout(() => {
                handleClose();
                onSuccess?.();
            }, 2000);

        } catch (err: any) {
            console.error("Add funds error:", err);

            if (err?.response?.data?.message) {
                setError(err.response.data.message);
            } else if (err?.response?.data?.error) {
                setError(err.response.data.error);
            } else if (err?.message) {
                setError(err.message);
            } else {
                setError(t("addFundsModal.unexpectedError"));
            }
        } finally {
            setLoading(false);
        }
    };

    const fees = calculateFees();

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
            style={{ backdropFilter: "blur(4px)" }}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <span className="text-2xl font-bold text-white">{getDynamicCurrencySymbol()}</span>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">{t("addFundsModal.title")}</h2>
                                <p className="text-blue-100 text-sm">{t("addFundsModal.subtitle")}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {success ? (
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-center py-8"
                        >
                            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("addFundsModal.fundsAddedSuccess")}</h3>
                            <div className="text-gray-600 space-y-2">
                                <p>{t("addFundsModal.walletFundedSuccess")}</p>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                                    <p className="text-sm text-green-800">
                                        <strong>{t("addFundsModal.amountAdded")}</strong> {getDynamicCurrencySymbol()}{formData.amount}
                                    </p>
                                    {/* <p className="text-sm text-green-800 mt-1">
                                        <strong>{t("addFundsModal.platformFeeLabel")}</strong> {getDynamicCurrencySymbol()}{fees.platformFee.toFixed(2)}
                                    </p> */}
                                    {fees.customerTax > 0 && (
                                        <p className="text-sm text-green-800 mt-1">
                                            <strong>{t("addFundsModal.customerTaxLabel")}</strong> {getDynamicCurrencySymbol()}{fees.customerTax.toFixed(2)}
                                        </p>
                                    )}
                                    <p className="text-sm text-green-800 mt-1">
                                        <strong>{t("addFundsModal.totalCharged")}</strong> {getDynamicCurrencySymbol()}{fees.total.toFixed(2)}
                                    </p>
                                </div>
                                <p className="text-xs text-gray-500 mt-3">
                                    {t("addFundsModal.fundsAvailable")}
                                </p>
                            </div>
                        </motion.div>
                    ) : (
                        <div className="space-y-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Amount */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("addFundsModal.amount")} <span className="text-red-500">{t("addFundsModal.required")}</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-400">{getDynamicCurrencySymbol()}</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="10"
                                            value={formData.amount}
                                            onChange={(e) => {
                                                setFormData({ ...formData, amount: e.target.value });
                                                setError(null);
                                            }}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${formData.amount && parseFloat(formData.amount) < 10
                                                ? "border-red-300"
                                                : "border-gray-300"
                                                }`}
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                    {formData.amount && parseFloat(formData.amount) < 10 && (
                                        <p className="text-red-500 text-xs mt-1">{t("addFundsModal.minimumAmountLabel", { symbol: getDynamicCurrencySymbol() })}</p>
                                    )}
                                    <p className="text-gray-500 text-xs mt-1">{t("addFundsModal.minimumLabel", { symbol: getDynamicCurrencySymbol() })}</p>
                                </div>

                                {/* Currency */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("addFundsModal.currency")}
                                        {isDetectingCurrency && (
                                            <span className="ml-2 text-xs text-blue-600">
                                                {t("addFundsModal.detectingCurrency")}
                                            </span>
                                        )}
                                    </label>
                                    <CurrencyDropdown
                                        selectedCurrency={selectedCurrency}
                                        onCurrencySelect={handleCurrencySelect}
                                        placeholder={t("addFundsModal.selectCurrencyPlaceholder")}
                                        disabled={isDetectingCurrency}
                                        autoDetect={true}
                                    />
                                    {isDetectingCurrency && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            {t("addFundsModal.autoDetectingCurrency")}
                                        </p>
                                    )}
                                </div>

                                {/* Payment Method */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("addFundsModal.paymentMethod")} <span className="text-red-500">{t("addFundsModal.required")}</span>
                                    </label>
                                    <div className="space-y-2">
                                        {paymentMethods.filter(method => 
                                            method.type !== "bank_account" && 
                                            (method.type as string) !== "us_bank_account" && 
                                            method.type !== "sepa_bank_account"
                                        ).length > 0 ? (
                                            paymentMethods
                                                .filter(method => 
                                                    method.type === "card"
                                                )
                                                .map((method) => (
                                                <label
                                                    key={method.id}
                                                    className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-colors ${formData.paymentMethodId === method.id
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <CreditCard className="h-5 w-5 text-gray-600" />
                                                        <div>
                                                            <div className="font-medium text-sm">
                                                                {method.type === "card" ? (
                                                                    `${method.brand?.toUpperCase()} **** ${method.last4}`
                                                                ) : (
                                                                    `${method.bankName} **** ${method.last4}`
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {method.type === "card" ? (
                                                                    t("addFundsModal.expires", { month: method.expMonth, year: method.expYear })
                                                                ) : (
                                                                    t("addFundsModal.paymentMethodLabel")
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value={method.id}
                                                        checked={formData.paymentMethodId === method.id}
                                                        onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
                                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                                    />
                                                </label>
                                            ))
                                        ) : (
                                            <div className="p-4 border border-gray-300 rounded-xl text-center">
                                                <p className="text-sm text-gray-600 mb-3">{t("addFundsModal.noPaymentMethods")}</p>
                                            </div>
                                        )}

                                        {/* Add Payment Method Buttons */}
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setShowAddCard(true)}
                                                className="flex-1 text-xs py-2"
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                {t("addFundsModal.addCard")}
                                            </Button>
                                            {/* <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setShowAddBankAccount(true)}
                                                className="flex-1 text-xs py-2"
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Add Bank
                                            </Button> */}
                                        </div>
                                    </div>
                                </div>

                                {/* Currency Conversion Info */}
                                {showConversionInfo && baseCurrencyId && formData.selectedCurrencyId !== baseCurrencyId && currencies && currencies.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                        <div className="flex items-start gap-2">
                                            <ArrowRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h3 className="text-sm font-medium text-blue-900 mb-2">Currency Conversion Applied</h3>
                                                <div className="text-xs text-blue-800 space-y-1">
                                                    {(() => {
                                                        if (formData.amount && parseFloat(formData.amount) > 0) {
                                                            const conversionResult = convertCurrency(
                                                                parseFloat(formData.amount),
                                                                baseCurrencyId,
                                                                formData.selectedCurrencyId,
                                                                currencies
                                                            );
                                                            const baseCurrency = getCurrencyById(baseCurrencyId, currencies);
                                                            const targetCurrency = getCurrencyById(formData.selectedCurrencyId, currencies);
                                                            return (
                                                                <>
                                                                    <p>
                                                                        Original: {getCurrencySymbol(baseCurrency?.initial || "USD")}{formData.amount} {baseCurrency?.initial || "USD"}
                                                                    </p>
                                                                    <p>
                                                                        Converted: {getCurrencySymbol(targetCurrency?.initial || "USD")}{conversionResult.convertedAmount.toFixed(2)} {targetCurrency?.initial || "USD"}
                                                                    </p>
                                                                    <p className="text-xs text-blue-600 mt-1">
                                                                        Exchange Rate: 1 {baseCurrency?.initial} = {conversionResult.exchangeRate.toFixed(4)} {targetCurrency?.initial}
                                                                    </p>
                                                                </>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Transaction Summary */}
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">{t("addFundsModal.transactionSummary")}</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">{t("addFundsModal.amountLabel")}</span>
                                            <span className="font-medium">{getDynamicCurrencySymbol()}{fees.amount.toFixed(2)}</span>
                                        </div>
                                        {/* <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">{t("addFundsModal.platformFee")}</span>
                                            <span className="font-medium">{getDynamicCurrencySymbol()}{fees.platformFee.toFixed(2)}</span>
                                        </div> */}
                                        {fees.customerTax > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">{t("addFundsModal.customerTax")}</span>
                                                <span className="font-medium">{getDynamicCurrencySymbol()}{fees.customerTax.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-sm font-medium border-t pt-2">
                                            <span>{t("addFundsModal.total")}</span>
                                            <span>{getDynamicCurrencySymbol()}{fees.total.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Error Message */}
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="bg-red-50 border border-red-200 rounded-lg p-4"
                                        >
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                    <div className="text-sm text-red-700">
                                                        {error}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClose}
                                        className="flex-1 h-12"
                                        disabled={loading}
                                    >
                                        {t("addFundsModal.cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={
                                            loading ||
                                            !formData.amount ||
                                            !formData.paymentMethodId ||
                                            parseFloat(formData.amount) < 10
                                        }
                                        className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                {t("addFundsModal.addingFunds")}
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-lg font-bold mr-2">{getDynamicCurrencySymbol()}</span>
                                                {t("addFundsModal.addFunds")}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Add Payment Method Modals */}
            <AnimatePresence>
                {showAddCard && (
                    <CardPaymentMethodForm
                        onClose={() => setShowAddCard(false)}
                        onSuccess={handlePaymentMethodAdded}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showAddBankAccount && (
                    <BankAccountPaymentMethodForm
                        onClose={() => setShowAddBankAccount(false)}
                        onSuccess={handlePaymentMethodAdded}
                    />
                )}
            </AnimatePresence>
        </div>,
        document.body
    );
};

export default AddFundsModal;
